import { useEffect } from "react";
import { FileDown } from "lucide-react";
import { useGetOne, useLocaleState, useTranslate } from "ra-core";
import { useParams } from "react-router";

import logoBlack from "@/assets/cmg360/logo-black.png";
import { formatDateOnly, formatMoney } from "../clients/clientModel";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Client, Company, Proposal } from "../types";
import { monthlyTotal } from "./proposalModel";

// Printable proposal (light document). "Download PDF" uses the browser's
// print dialog, so the PDF is generated locally with no external service.
export const ProposalPrintPage = () => {
  const { id } = useParams();
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const { dealCategories } = useConfigurationContext();
  const { data: proposal } = useGetOne<Proposal>("proposals", { id: id ?? 0 });
  const { data: company } = useGetOne<
    Company & Pick<Client, "main_contact_name">
  >(
    "companies",
    { id: proposal?.company_id ?? 0 },
    { enabled: proposal != null },
  );

  useEffect(() => {
    if (!proposal) return;
    const previousTitle = document.title;
    document.title = proposal.title;
    return () => {
      document.title = previousTitle;
    };
  }, [proposal]);

  if (!proposal) return null;

  const serviceLabel = (value: string) =>
    dealCategories.find((category) => category.value === value)?.label ?? value;
  // The document date is when it was sent (or created, while it is a draft).
  const issued = new Date(proposal.sent_at ?? proposal.created_at);
  const todayKey = `${issued.getFullYear()}-${String(issued.getMonth() + 1).padStart(2, "0")}-${String(issued.getDate()).padStart(2, "0")}`;

  return (
    <div className="min-h-screen bg-[#ececec] py-10 print:bg-white print:py-0">
      <style>{`@page { size: A4; margin: 18mm 16mm; } @media print { html, body { background: #fff !important; } }`}</style>
      <div className="mx-auto mb-6 flex max-w-[794px] justify-end print:hidden">
        <button
          type="button"
          onClick={() => window.print()}
          className="flex items-center gap-2 rounded-md bg-[#111] px-4 py-2 text-sm font-medium text-white hover:bg-black"
        >
          <FileDown className="size-4" />
          {translate("resources.proposals.download_pdf")}
        </button>
      </div>
      <ProposalDocument
        proposal={proposal}
        companyName={company?.name ?? ""}
        attention={company?.main_contact_name ?? null}
        serviceLabel={serviceLabel}
        issuedKey={todayKey}
        locale={locale}
        t={(key, options) =>
          translate(`resources.proposals.document.${key}`, options)
        }
      />
    </div>
  );
};

// The document itself: black on white, laid out like a formal quote
// (numbered header, details block, services table, totals, sign-off).
export const ProposalDocument = ({
  proposal,
  companyName,
  attention,
  serviceLabel,
  issuedKey,
  locale,
  t,
}: {
  proposal: Proposal;
  companyName: string;
  attention: string | null;
  serviceLabel: (value: string) => string;
  issuedKey: string;
  locale: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}) => {
  const total = monthlyTotal(proposal.items);
  const money = (value: number) => formatMoney(value, "COP", locale);
  const number = `P-${String(proposal.id).padStart(4, "0")}`;

  return (
    <article className="mx-auto max-w-[794px] bg-white px-16 py-16 text-[13px] leading-relaxed text-[#111] shadow-[0_1px_3px_rgba(0,0,0,0.08)] print:max-w-none print:p-0 print:shadow-none">
      <header className="flex items-start justify-between gap-8">
        <img src={logoBlack} alt="CMG360" className="h-11 w-auto" />
        <div className="text-right">
          <div className="text-[15px] font-semibold">{t("heading")}</div>
          <div className="mt-0.5 tabular-nums text-[#6b6b6b]">{number}</div>
        </div>
      </header>

      <dl className="mt-14 grid grid-cols-3 gap-8 border-t border-[#111] pt-5">
        <div>
          <dt className="text-[11px] text-[#6b6b6b]">{t("prepared_for")}</dt>
          <dd className="mt-1 font-semibold">{companyName}</dd>
          {attention ? (
            <dd className="text-[#444]">
              {t("attention")} {attention}
            </dd>
          ) : null}
        </div>
        <div>
          <dt className="text-[11px] text-[#6b6b6b]">{t("date")}</dt>
          <dd className="mt-1">{formatDateOnly(issuedKey, locale)}</dd>
        </div>
        <div>
          <dt className="text-[11px] text-[#6b6b6b]">{t("valid_until")}</dt>
          <dd className="mt-1">
            {proposal.valid_until
              ? formatDateOnly(proposal.valid_until, locale)
              : "—"}
          </dd>
        </div>
      </dl>

      <section className="mt-14">
        <h1 className="text-[26px] font-semibold leading-tight tracking-[-0.01em]">
          {proposal.title}
        </h1>
        {proposal.intro ? (
          <p className="mt-4 max-w-[560px] whitespace-pre-line text-[14px] text-[#333]">
            {proposal.intro}
          </p>
        ) : null}
      </section>

      <table className="mt-12 w-full border-collapse">
        <thead>
          <tr className="border-b border-[#111] text-left text-[11px] text-[#6b6b6b]">
            <th className="pb-2 font-normal">{t("service")}</th>
            <th className="whitespace-nowrap pb-2 text-right font-normal">
              {t("monthly_value")}
            </th>
          </tr>
        </thead>
        <tbody>
          {proposal.items.map((item, index) => (
            <tr
              key={index}
              className="border-b border-[#e5e5e5] align-top break-inside-avoid"
            >
              <td className="py-4 pr-10">
                <div className="font-semibold">
                  {serviceLabel(item.service)}
                </div>
                {item.description ? (
                  <p className="mt-1 whitespace-pre-line text-[#555]">
                    {item.description}
                  </p>
                ) : null}
              </td>
              <td className="py-4 text-right tabular-nums whitespace-nowrap">
                {money(item.monthly_price)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-4 flex justify-end break-inside-avoid">
        <dl className="w-[320px]">
          <div className="flex justify-between border-t border-[#111] pt-2.5 text-[15px] font-semibold">
            <dt>{t("total")}</dt>
            <dd className="tabular-nums">{money(total)}</dd>
          </div>
          {proposal.setup_fee ? (
            <div className="mt-1.5 flex justify-between text-[#444]">
              <dt>{t("setup")}</dt>
              <dd className="tabular-nums">{money(proposal.setup_fee)}</dd>
            </div>
          ) : null}
        </dl>
      </div>

      {proposal.terms ? (
        <section className="mt-14 break-inside-avoid">
          <h2 className="text-[13px] font-semibold">{t("terms")}</h2>
          <p className="mt-2 whitespace-pre-line text-[#444]">
            {proposal.terms}
          </p>
        </section>
      ) : null}

      <section className="mt-14 break-inside-avoid">
        <h2 className="text-[13px] font-semibold">{t("acceptance")}</h2>
        <p className="mt-2 text-[#444]">{t("acceptance_text")}</p>
        <div className="mt-12 grid grid-cols-2 gap-12">
          {["CMG360", companyName].map((party) => (
            <div key={party}>
              <div className="border-t border-[#111] pt-2 font-semibold">
                {t("sign_for", { name: party })}
              </div>
              <div className="mt-1 text-[11px] text-[#6b6b6b]">
                {t("sign_fields")}
              </div>
            </div>
          ))}
        </div>
      </section>

      <footer className="mt-16 flex justify-between border-t border-[#e5e5e5] pt-4 text-[11px] text-[#6b6b6b]">
        <span>{t("contact")}</span>
        <span className="tabular-nums">{number}</span>
      </footer>
    </article>
  );
};

ProposalPrintPage.path = "/proposals/:id/print";
